<template>
  <div class="destination-phone-verification">
    <div v-if="props.phoneNumber" class="destination-phone-verification__number">
      <b><MaskedPhoneNumber :phoneNumber="props.phoneNumber" /></b>
      <span v-if="props.isVerified" class="destination-phone-verification__status destination-phone-verification__status--verified">
        <IconEmbedded name="check_3-5" :size="15" />
        Verified
      </span>
      <span v-else class="destination-phone-verification__status">Not verified yet</span>
    </div>

    <SrpButton size="tiny" :fill="props.isVerified ? 'text' : 'outlined'" @click="isVerificationModalVisible = true">
      {{ buttonText }}
    </SrpButton>

    <!-- Shrpa team can't receive the destination's text, so they're allowed to enter the number unverified -->
    <div v-if="userProfileStore.isSuperOrSalesUser" class="destination-phone-verification__super-entry">
      <LinkWithIcon isDottedUnderline @click="isManualEntryVisible = !isManualEntryVisible">
        <span>enter without verifying (super)</span>
      </LinkWithIcon>
      <!-- SrpPhoneNumberInput emits when it mounts (before anything is typed), so only pass along changes that follow real input -->
      <div v-if="isManualEntryVisible" class="destination-phone-verification__super-input" @input="hasTypedManually = true">
        <SrpPhoneNumberInput :modelValue="props.phoneNumber ?? ''" @update:modelValue="onManualEntry" />
      </div>
    </div>

    <SrpModal v-model:isVisible="isVerificationModalVisible" :zIndex="400" size="small">
      <template #content>
        <PhoneNumberVerificationForm
          v-if="isVerificationModalVisible"
          :initialPhoneNumber="props.phoneNumber"
          :verificationApiBaseUri="verificationApiBaseUri"
          @phoneVerified="onPhoneVerified"
          @verificationCanceled="isVerificationModalVisible = false"
        />
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

// Types
import { CollabInput } from "@contracts/collab";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import MaskedPhoneNumber from "@components/MaskedPhoneNumber.vue";
import PhoneNumberVerificationForm from "@components/PhoneNumberVerificationForm.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpPhoneNumberInput from "@components/ui/SrpPhoneNumberInput.vue";

// Stores
import { useUserProfileStore } from "@stores/userProfileStore";
const userProfileStore = useUserProfileStore();

// Shows the destination contact's phone number for a Creator Visit and lets them text-verify it (or change it, which re-verifies).
// The server saves the number to the collab when the code is confirmed, so "verified" hands back the updated collab.
const props = withDefaults(
  defineProps<{
    customerId: string;
    collabInputId: string;
    phoneNumber: string | null;
    isVerified: boolean;
  }>(),
  {
    phoneNumber: null,
    isVerified: false,
  }
);

const emit = defineEmits<{
  // The collab as saved by the server (new number, verified flag and _etag)
  (e: "verified", updatedCollabInput: CollabInput): void;
  // Super-only: the number was typed in without being verified
  (e: "update:phoneNumber", value: string): void;
}>();

const verificationApiBaseUri = computed<string>(() => `${import.meta.env.VITE_API_URL}/collabs/${props.customerId}/inputs/${props.collabInputId}/destination-contact`);

const buttonText = computed<string>(() => {
  if (props.isVerified) return "Change phone number";
  return props.phoneNumber ? "Verify phone number" : "Provide a phone number";
});

// Verification modal =========================================================
const isVerificationModalVisible = ref<boolean>(false);

function onPhoneVerified(phoneNumber: string, responseData: { collabInput: CollabInput }) {
  isVerificationModalVisible.value = false;
  isManualEntryVisible.value = false;
  emit("verified", responseData.collabInput);
}

// Manual entry (super-only) ==================================================
const isManualEntryVisible = ref<boolean>(false);
const hasTypedManually = ref<boolean>(false);

function onManualEntry(value: string) {
  if (!hasTypedManually.value) return;
  emit("update:phoneNumber", value);
}
</script>

<style scoped lang="scss">
.destination-phone-verification {
  &__number {
    margin-bottom: 8px;
    gap: 10px;
    display: flex;
    align-items: center;
    color: rgba(0, 0, 0, 1);
    font-family: sans-serif;
  }

  &__status {
    gap: 3px;
    display: inline-flex;
    align-items: center;
    color: firebrick;
    font-size: 13px;

    &--verified {
      color: #549e3f;

      :deep(svg) {
        fill: #549e3f;
      }
    }
  }

  &__super-entry {
    margin-top: 10px;
    opacity: 0.6;
  }

  &__super-input {
    max-width: 320px;
    margin-top: 6px;
  }
}
</style>
